/** Default: hide level changes of 10 percentage points or less. */
export const DEFAULT_MIN_SILO_LEVEL_VARIATION_PERCENT = 10;

/**
 * Company setting: settings.client_frontend.min_silo_level_variation_percent
 * Absolute percentage points of silo level (nível). 0 disables the filter.
 * Spreadsheet and PDF reports keep the original reading interval. The level,
 * weight and volume stay put until the level moves more than this value, so a
 * smaller swing is not shown as a change. The on-screen history chart keeps
 * every reading as measured.
 * The older input_output_consume_graph_min_significant_delta_percent still
 * overrides the entradas/saídas summary when it is set explicitly.
 */
export function getMinSiloLevelVariationPercent(companySettings) {
  const raw = companySettings?.settings?.client_frontend?.min_silo_level_variation_percent;
  if (raw == null || raw === '') return DEFAULT_MIN_SILO_LEVEL_VARIATION_PERCENT;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0) return DEFAULT_MIN_SILO_LEVEL_VARIATION_PERCENT;
  return value;
}

function readingLevel(profile) {
  const level = Number(profile?.level ?? profile?.y);
  return Number.isFinite(level) ? level : null;
}

function readingTime(profile) {
  const raw = profile?.datetime_fetched ?? profile?.x ?? profile?.last_updated ?? null;
  if (raw == null) return null;
  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : null;
}

function holdBaseline(baselineProfile, profile) {
  return {
    ...baselineProfile,
    datetime_fetched: profile.datetime_fetched ?? baselineProfile.datetime_fetched,
    id: profile.id ?? baselineProfile.id,
  };
}

/**
 * Keeps every reading at its original timestamp. Level, weight and volume
 * change only when the level moves more than `minPercent` percentage points
 * from the last significant reading. Smaller swings repeat that reading.
 * `minPercent` <= 0 returns the measured values unchanged.
 */
export function filterProfilesByMinLevelVariation(profiles, minPercent = DEFAULT_MIN_SILO_LEVEL_VARIATION_PERCENT) {
  if (!Array.isArray(profiles) || profiles.length === 0) return [];
  const threshold = Number(minPercent);
  if (!Number.isFinite(threshold) || threshold <= 0) return profiles;

  const sorted = [...profiles].sort((a, b) => (readingTime(a) ?? 0) - (readingTime(b) ?? 0));
  const rows = [];
  let baseline = null;

  sorted.forEach((profile) => {
    const level = readingLevel(profile);
    if (level == null) {
      rows.push(profile);
      return;
    }
    if (baseline == null || Math.abs(level - baseline.level) > threshold) {
      baseline = { level, profile };
      rows.push(profile);
      return;
    }
    rows.push(holdBaseline(baseline.profile, profile));
  });

  return rows;
}

/**
 * True when a level delta should be treated as a real entrada or saída.
 * Comparison is in percentage points of nível, not relative to the current level.
 */
export function isSignificantLevelVariation(deltaLevel, minPercent = DEFAULT_MIN_SILO_LEVEL_VARIATION_PERCENT) {
  const threshold = Number(minPercent);
  if (!Number.isFinite(threshold) || threshold <= 0) return true;
  const delta = Number(deltaLevel);
  if (!Number.isFinite(delta)) return false;
  return Math.abs(delta) > threshold;
}
